import { chooseCardSpot } from "./cardSpot";
import { chooseFlowCardSpot, Rect } from "./flowCardSpot";
import * as d3 from "d3";
import { each } from "lodash";
import {
  getBubbleIconUrl,
  getGoalStarIconUrl,
  getHistoricalIconUrl,
  getHistoricalPercentBase,
  resolveRealtimeCategory,
  isSampledPlaybackView,
  selectedDateRange,
  selectedFilter,
  setColor,
} from "./clusterService";
import { historical } from "./metricsModal";
import { cleanLink, formatUnderscoredLabel, isNoDataValue, joinResolvedGeo, needsReadableLabel, readableLabel } from "./helper";
import { visitLabel } from "./ordinal";
import * as playbackModeService from "./playbackModeService";
import { FLOW_TOOLTIP_OFFSET } from "./flowTooltips";

// Redesigned tooltip content (2026-07-10 mockup) - shared by both the
// real-time bubble and historical cluster tooltips. Header is always two
// columns divided by a vertical rule: real-time pins "New/Return" on the
// left (regardless of which filter is active - New/Return is a labeling
// choice, not "Visitor Type", specifically to avoid colliding with the
// separate bot/AI "Visitor Type" filter that "New vs Returning" was
// renamed away from earlier this session) and shows the currently active
// filter's value on the right; historical shows the active filter on the
// left and the selected date range on the right. Body rows are label-left/
// value-right, with an optional lighter "sub" value (version numbers,
// percentages) rendered next to the main value.
interface TooltipRow {
  label: string;
  value: string | number;
  sub?: string;
}

// The tooltip header columns are narrow - a filter's real display name
// (used as-is everywhere else: the "By..." dropdown, the button label) can
// be too long there and wrap to a second line, pushing the rest of the
// tooltip down. Only abbreviates the HEADER copy, not the filter itself.
const HEADER_LABEL_ABBREVIATIONS: Record<string, string> = {
  "Operating System": "Oper. System",
};
const abbreviateHeaderLabel = (label: string) =>
  HEADER_LABEL_ABBREVIATIONS[label] ?? label;

const buildTooltipHtml = (
  headerLeftLabel: string,
  headerLeftValue: string,
  headerRightLabel: string,
  headerRightValue: string,
  rows: TooltipRow[],
  iconUrl?: string,
  // Real-time only (historical shows its own conversion count/rate as a
  // normal body row instead - see showHistoricalTooltip). Was a full-width
  // colored banner - reported as visually heavy - now a small top-left
  // marker instead: the same white star asset the bubble's own badge uses,
  // plus a small uppercase label, no background/shadow/divider of its own.
  converted?: boolean
) => {
  const rowsHtml = rows
    .filter((row) => row.value !== undefined && row.value !== null && row.value !== "")
    .map(
      (row) => `
        <div class="tooltip-row">
          <span class="tooltip-row-label">${row.label}</span>
          <span class="tooltip-row-value">${row.value}${
        row.sub ? `<span class="tooltip-row-sub">${row.sub}</span>` : ""
      }</span>
        </div>`
    )
    .join("");

  // Icon mode: rather than a separate white-background/dark-text variant
  // (the old behavior, which only existed because a solid category color
  // and an icon-mode bubble used to feel visually mismatched), this keeps
  // the same colored background/white text everywhere - the category
  // accent color is still meaningful even when the bubble itself renders
  // as an icon - and just overlays the small icon in the corner as before,
  // for a quick visual confirmation on top of it.
  const iconHtml = iconUrl
    ? `<img src="${iconUrl}" class="tooltip-icon" />`
    : "";

  const convertedHtml = converted
    ? `<div class="tooltip-converted-badge">
        <img src="${getGoalStarIconUrl()}" class="tooltip-converted-star" />
        <span>Goal converted</span>
      </div>`
    : "";

  return `
    ${convertedHtml}
    <div class="tooltip-header">
      <div class="tooltip-header-col">
        <span class="tooltip-header-label">${headerLeftLabel}</span>
        <span class="tooltip-header-value">${headerLeftValue}</span>
      </div>
      <div class="tooltip-header-col">
        <span class="tooltip-header-label">${headerRightLabel}</span>
        <span class="tooltip-header-value">${headerRightValue}</span>
      </div>
    </div>
    <div class="tooltip-body">${rowsHtml}</div>
    ${iconHtml}
  `;
};

export const setTooltipView = (userInfo) => {
  let html =
  "<div style=\"color:  transparent !important; background: inherit !important; -webkit-filter: grayscale(1) invert(1) brightness(128%) contrast(999) !important; filter:  grayscale(1) invert(1) brightness(128%) contrast(999) !important; -webkit-background-clip: text !important; background-clip: text !important;\">";
  each(userInfo, function (value, key) {
    html = html + key.toUpperCase() + " : " + value + "</br>";
  });


  return (html = html + "</div>");
};

// Icon mode tooltip: the colored-background + inverted-text trick above only
// makes sense against an arbitrary saturated color. In icon mode the bubble
// itself already shows the icon, so the tooltip goes plain white with normal
// dark text and just a small copy of the same icon in the corner for quick
// visual confirmation, rather than tinting the whole box.
export const setIconTooltipView = (userInfo, iconUrl?: string) => {
  let html = "<div style=\"color: #333; padding-right: 22px;\">";
  each(userInfo, function (value, key) {
    html = html + key.toUpperCase() + " : " + value + "</br>";
  });
  html += "</div>";
  if (iconUrl) {
    html += `<img src="${iconUrl}" style="position: absolute; top: 10px; right: 10px; width: 22px; height: 16px; object-fit: contain; border-radius: 2px;" />`;
  }
  return html;
};

// Tooltips are positioned from the hovered bubble's coordinates with no
// awareness of viewport edges, so a bubble near the right/bottom/top of the
// screen produced a tooltip that partially rendered off-screen (clipped by
// the app's overflow:hidden container, unreadable). Called right after the
// left/top styles are set (d3 .style() writes are synchronous, so the
// browser has already laid it out by the time getBoundingClientRect runs
// here) - nudges the tooltip back inside the viewport by whatever amount it
// overflows, on whichever edges it overflows. Works for both plain
// top/left-anchored tooltips and the historical one's translateY(-100%)
// anchor, since getBoundingClientRect already accounts for transforms.
const clampTooltipToViewport = () => {
  const tooltip = document.getElementById("tooltip");
  if (!tooltip) {
    return;
  }
  const margin = 8;
  const rect = tooltip.getBoundingClientRect();

  // The app header (Header.tsx's ".header") is a normal-flow element sitting
  // above #main_svg (the tooltip's positioned ancestor), not an overlay - so
  // a bubble in the chart's top row, whose tooltip anchors above it, can end
  // up with a negative top relative to #main_svg that's still >0 in viewport
  // terms (window.innerHeight top) but lands underneath/inside the header's
  // own box. Using the header's actual bottom edge as the top boundary
  // (instead of just window top) keeps the tooltip below it rather than
  // merely inside the window.
  const header = document.querySelector(".header");
  // a recipient's viewer (src/share/): the stage is its own scrolling area between the top bar and the controls, and the card lives inside it, so the card is kept inside THAT area (it was cut off by the bar,
  // then would have been clipped at the area's edge)
  const shareStage = document.querySelector("[data-share-main]");
  const stageRect = shareStage ? shareStage.getBoundingClientRect() : null;
  const minTop = stageRect
    ? stageRect.top + margin
    : header
    ? header.getBoundingClientRect().bottom + margin
    : margin;
  const maxBottom = stageRect ? stageRect.bottom - margin : window.innerHeight - margin;

  let deltaX = 0;
  let deltaY = 0;
  if (rect.right > window.innerWidth - margin) {
    deltaX = window.innerWidth - margin - rect.right;
  }
  if (rect.left + deltaX < margin) {
    deltaX = margin - rect.left;
  }
  if (rect.bottom > maxBottom) {
    deltaY = maxBottom - rect.bottom;
  }
  if (rect.top + deltaY < minTop) {
    deltaY = minTop - rect.top;
  }
  if (deltaX !== 0 || deltaY !== 0) {
    const currentLeft = parseFloat(tooltip.style.left) || 0;
    const currentTop = parseFloat(tooltip.style.top) || 0;
    tooltip.style.left = currentLeft + deltaX + "px";
    tooltip.style.top = currentTop + deltaY + "px";
  }
};

/**
 * The donut: the card follows the pointer, which is over the ring, so it landed on the ring and hid the slices (Chris, 2026-10-24). If the card overlaps the ring it is moved sideways to just outside it, on
 * the side the card was already on.
 */
const keepTooltipOffDonut = (tooltip: HTMLElement): void => {
  const track = document.querySelector("g.historical-donut .hdonut-track");
  if (!track) return;
  const rr = track.getBoundingClientRect();
  const r = parseFloat(track.getAttribute("r") || "0");
  if (!r || !rr.width) return;
  const scale = rr.width / (2 * r);
  const cx = rr.left + rr.width / 2, cy = rr.top + rr.height / 2;
  const outer = rr.width / 2 + (parseFloat(track.getAttribute("stroke-width") || "0") * scale) / 2 + 6;
  const box = tooltip.getBoundingClientRect();
  const nx = Math.max(box.left, Math.min(cx, box.right)), ny = Math.max(box.top, Math.min(cy, box.bottom));
  if (Math.hypot(nx - cx, ny - cy) >= outer) return;
  const toRight = box.left + box.width / 2 >= cx;
  const delta = toRight ? cx + outer - box.left : cx - outer - box.right;
  tooltip.style.left = (parseFloat(tooltip.style.left) || 0) + delta + "px";
};

/**
 * A visitor's card is placed beside the pointer, which is often inside a crowd of other visitors (Chris, 2026-10-24: it hid the bubbles in the corner). Of the four places beside the pointer (right or left,
 * below or above) the card takes the one that covers the fewest visitors; the usual place wins a tie, so nothing moves unless it would help.
 */
const keepTooltipOffVisitors = (tooltip: HTMLElement, pointerX: number, pointerY: number, hoveredId: string | null | undefined): void => {
  // (in icon mode a visitor is its icon image; the shape under it is invisible, so the images are what is counted, once per visitor)
  const seen = new Set<string>();
  const others = Array.from(document.querySelectorAll("svg.svg-main-window image[realTime=true], svg.svg-main-window circle[realTime=true], svg.svg-main-window rect[realTime=true]"))
    .filter((el) => {
      const id = el.getAttribute("visitor_id") || "";
      if (id && (id === hoveredId || seen.has(id + (el.tagName === "image" ? "i" : "s")))) return false;
      seen.add(id + (el.tagName === "image" ? "i" : "s"));
      return true;
    })
    .map((el) => el.getBoundingClientRect())
    .filter((r) => r.width > 0);
  if (others.length === 0) return;
  const box = tooltip.getBoundingClientRect();
  const header = document.querySelector(".header");
  const spot = chooseCardSpot({
    box: { left: box.left, top: box.top, width: box.width, height: box.height },
    pointer: { x: pointerX, y: pointerY },
    others,
    view: { width: window.innerWidth, height: window.innerHeight, top: header ? header.getBoundingClientRect().bottom + 8 : 8 },
  });
  if (!spot) return;
  tooltip.style.left = (parseFloat(tooltip.style.left) || 0) + (spot.l - box.left) + "px";
  tooltip.style.top = (parseFloat(tooltip.style.top) || 0) + (spot.t - box.top) + "px";
};

export const rgbToHex = (rgb) => {
 
  return (
    "#" +
    ((1 << 24) + (rgb.r << 16) + (rgb.g << 8) + rgb.b).toString(16).slice(1)
  );
};
// Historical body rows beyond Visitors/% (New Visitors, Sessions, Bounce
// Rate, Pages/Session, Avg Session Duration) are Pass 2 - none of those are
// computed per-category anywhere on the backend yet (only site-wide, via
// getMainMetrics), just site-wide totals that would be misleading to show
// on a single category's tooltip. Deliberately left out rather than shown
// wrong.
export const showHistoricalTooltip = (bubbleData) => {
  if (suppressForTouch()) {
    return;
  }
  // storage.get("totalSession") used to be the denominator here, but that
  // value is only ever set by Footer.tsx's own separate metrics fetch (and
  // cleared to "" elsewhere) - completely disconnected from the dock's
  // actual running total, and easily 0/empty at the moment a bubble is
  // hovered (e.g. right after a playback filter-change dock rebuild).
  // Dividing by 0 produces Infinity, which is truthy - the `|| 0` fallback
  // below only catches NaN/falsy results, not Infinity, so it rendered
  // literally as "Infinity%". historical.totalSession (from metricsModal.ts)
  // is the value actually incremented in lockstep with every dock bubble
  // (see clusterService.ts's applyDepartedSessionsBulk and
  // moveBubbleToHistoricalCluster), so it can never disagree with what's
  // literally on screen.
  // The dock bubble's own count is what was played. On a SAMPLED playback day that differs from the day's real total, and says so.
  const sampledView = isSampledPlaybackView();
  const sessionsShown = bubbleData.target.__data__.user;
  // With "Hide unknown" on the base is the KNOWN groups only (getHistoricalPercentBase), so what is shown adds up to 100% of what is known.
  const totalSession = getHistoricalPercentBase();
  const calculatedPercent = totalSession ? (sessionsShown / totalSession) * 100 : 0;

  const iconUrl = getHistoricalIconUrl(bubbleData.target.__data__);

  // Per-category conversion metrics (metrics.ts's getHistoricalBubblesData,
  // computed in the same aggregation pass as the visitor count - see its
  // own comment). Percent is this category's SHARE OF ALL CONVERSIONS
  // (converted / historical.totalConverted) - matching how the Sessions
  // row above already shows share of ALL sessions, not a rate internal to
  // this one category. Dividing by this category's own session count
  // instead (the original version) meant a country with a single session
  // that happened to convert showed "1 converted (100%)" even when it was
  // only 1 of, say, 5 conversions site-wide - reported directly as
  // obviously wrong math, and it was.
  const convertedCount = bubbleData.target.__data__.converted || 0;
  const convertedPercent = historical.totalConverted
    ? (convertedCount / historical.totalConverted) * 100
    : 0;

  // Same "/" ambiguity as the sidebar list/Pages tab/page boxes (see those
  // components' own comments) - here specifically it's the Referral Link
  // filter's "no real referrer" bucket. Not muted gray here (unlike those
  // other spots) since this tooltip already uses the same normal text
  // color throughout, with no separate "secondary" styling convention to
  // hook into.
  const headerLeftValue =
    selectedFilter?.menuType === "referrer" &&
    bubbleData.target.__data__.menuName === "/"
      ? "/  Direct"
      : readableLabel(selectedFilter?.menuType, bubbleData.target.__data__.menuName);

  const html = buildTooltipHtml(
    abbreviateHeaderLabel(selectedFilter?.name ?? ""),
    headerLeftValue,
    "Overall",
    playbackModeService.priorLabel || (selectedDateRange?.name ?? ""),
    [
      {
        // Labeled "Sessions" not "Visitors" - this counts Session
        // documents in this category/date-range, and one person can have
        // multiple sessions (no persistent cross-session visitor identity
        // exists yet), so "Visitors" would overstate unique reach.
        label: sampledView ? "Sessions (sampled total)" : "Sessions",
        value: sessionsShown,
        sub: (calculatedPercent || 0).toFixed(2) + "%",
      },
      // Only shown when this category actually has a conversion - most
      // categories won't, and an always-present "Converted: 0 (0%)" row
      // would be noise on every single tooltip.
      ...(convertedCount > 0
        ? [
            {
              label: "Converted",
              value: convertedCount,
              sub: convertedPercent.toFixed(1) + "%",
            },
          ]
        : []),
    ],
    iconUrl
  );

  // Cancel any in-flight fade-out transition from a tooltip we just left -
  // otherwise that transition keeps overwriting opacity on every frame,
  // fighting the 0.9 set below and making this tooltip render faded until
  // the old fade finishes.
  d3.select("#tooltip").interrupt();

  d3.select("#tooltip")
    .attr("class", "historical-cluster-tooltip tooltip-v2")
    .html(html)
    .style("opacity", 1)
    .style("left", bubbleData.layerX + bubbleData.target.__data__.radius + "px")
    // Was `top: layerY - radius` with no transform - since `top` sets the
    // element's TOP edge, the tooltip grew downward-right from that point,
    // i.e. rendered below-right of the bubble. `translateY(-100%)` shifts
    // it up by its own full rendered height (robust regardless of content
    // length, unlike subtracting a guessed pixel offset), flipping the
    // anchor to the tooltip's BOTTOM edge instead - so it now grows
    // upward-right, appearing top-right of the bubble as requested. The
    // extra -8px on top leaves a small gap above the bubble instead of the
    // tooltip's bottom edge touching it directly.
    .style("top", bubbleData.layerY - bubbleData.target.__data__.radius - 8 + "px")
    .style("transform", "translateY(-100%)")
    .style("background-color", rgbToHex(bubbleData.target.__data__.colorInfo))
    .style("border", "none");

  const cardNode = document.getElementById("tooltip");
  if (cardNode) keepTooltipOffDonut(cardNode);
  clampTooltipToViewport();

  d3.select("#tooltip").transition().duration(200);
};

// Location/OS version/Referrer were already captured on the backend but
// stripped out of the trimmed sessionData object attached to each bubble
// (clusterService.ts's drawUserBubble) - added there so they're available
// here. Browser version, per-session Pages Visited, and Time on (current)
// Page are Pass 2 - none of those are tracked anywhere yet, real vs
// site-wide isn't the issue here, the data simply doesn't exist. IP Address
// is deliberately excluded - captured on the backend but privacy-sensitive
// with limited value for a default-visible field; not shown here.
/*
 * 🔴 HOVER TOOLTIPS MUST NOT FIRE ON TOUCH (2026-09-10).
 *
 * A tap on a touch device synthesises a `mouseover`, so tapping a bubble both
 * opened the drawer AND showed the hover tooltip - and `mouseout` never
 * arrives, because a finger does not move away from what it touched. The
 * tooltip therefore stuck permanently, surviving taps elsewhere. Reproduced
 * directly: opacity 0 before the tap, 1 after, still 1 after tapping empty
 * space.
 *
 * `(hover: none)` is the browser's own answer to "can this pointer hover",
 * which beats sniffing for touch support - a laptop with a touchscreen still
 * has a real pointer and should keep its hover tooltips.
 *
 * Long-press sets allowTouchTooltip so the SAME tooltip can be shown
 * deliberately; see attachTouchTooltips in clusterService.ts.
 */
export const isCoarsePointer = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(hover: none)").matches;

let allowTouchTooltip = false;
export const setTouchTooltipAllowed = (allowed: boolean) => {
  allowTouchTooltip = allowed;
};
// True when a hover-driven tooltip should be ignored: a coarse pointer that
// has not explicitly long-pressed.
const suppressForTouch = () => isCoarsePointer() && !allowTouchTooltip;

export const showRealtimeTooltip = (bubbleData) => {
  if (suppressForTouch()) {
    return;
  }
  const sessionData = bubbleData.target.__data__.sessionData;

  // .filter(Boolean) alone was not enough: LocationService writes the literal
  // string 'UNKNOWN' rather than leaving the field empty, and 'UNKNOWN' is
  // truthy - so this rendered "UNKNOWN, UNKNOWN" for the ~29% of sessions
  // whose range has no city-level data. joinResolvedGeo drops both.
  const location = joinResolvedGeo(sessionData.city, sessionData.region_name);

  // Session Time Total: no backend running-total field exists, but the
  // bubble's own createdAt is - a one-off snapshot computed at hover time
  // (not a ticking live value, this tooltip only renders while hovering).
  const createdAt = bubbleData.target.__data__.createdAt;
  let sessionTimeTotal;
  if (createdAt) {
    // in a replay the clock is the replay's own (a visit's time on the site so far at the moment shown), never the wall clock
    const nowMs = (playbackModeService.mode === "playback" ? playbackModeService.playbackClockMs() : null) ?? Date.now();
    const elapsedMs = nowMs - new Date(createdAt).getTime();
    const totalSeconds = Math.max(0, Math.floor(elapsedMs / 1000));
    const hh = String(Math.floor(totalSeconds / 3600)).padStart(2, "0");
    const mm = String(Math.floor((totalSeconds % 3600) / 60)).padStart(2, "0");
    const ss = String(totalSeconds % 60).padStart(2, "0");
    sessionTimeTotal = `${hh}:${mm}:${ss}`;
  }

  // Pinned "New/Return" - deliberately NOT labeled "Visitor Type" even
  // though the original mockup used that label, since "Visitor Type" is
  // also the name of the separate bot/AI/human filter (see the rename
  // earlier this session that freed up that name specifically) - using it
  // here too would collide when Visitor Type (bot/AI) is the active filter
  // on the right.
  const iconUrl = getBubbleIconUrl(bubbleData.target.__data__);

  // Same "/" ambiguity as the historical tooltip/sidebar list/Pages tab/
  // page boxes (see those components' own comments) - only relevant when
  // the active filter is Referral Link, whose raw category value for
  // direct traffic is the literal "/". Not muted gray here per the same
  // reasoning as the historical tooltip above.
  const realtimeHeaderRightValue =
    selectedFilter?.menuType === "referrer" &&
    resolveRealtimeCategory(sessionData) === "/"
      ? "/  Direct"
      : (needsReadableLabel(selectedFilter?.menuType) || isNoDataValue(resolveRealtimeCategory(sessionData))) && resolveRealtimeCategory(sessionData)
      ? readableLabel(selectedFilter?.menuType, resolveRealtimeCategory(sessionData) as string)
      : resolveRealtimeCategory(sessionData) ?? "-";
  const referrerValue =
    sessionData.referrer === "/" ? "/  Direct" : cleanLink(sessionData.referrer);

  const html = buildTooltipHtml(
    "New/Return",
    // "Return", or "Return · 3rd visit" once the server has counted it (the word itself is unchanged)
    sessionData.visitor_type
      ? [
          formatUnderscoredLabel(sessionData.visitor_type),
          sessionData.visitor_type === "return" ? visitLabel(sessionData.visitNumber, "visit") : "",
        ]
          .filter(Boolean)
          .join(" · ")
      : "-",
    abbreviateHeaderLabel(selectedFilter?.name ?? ""),
    realtimeHeaderRightValue,
    [
      { label: "Page Visiting", value: bubbleData.target.__data__.currentUrlName },
      { label: "Country", value: sessionData.country_name },
      { label: "Location", value: location },
      { label: "Browser", value: sessionData.browser },
      { label: "Oper. System", value: sessionData.os, sub: sessionData.os_version },
      { label: "Device Type", value: sessionData.device },
      { label: "Traffic Source", value: sessionData.traffic_source },
      { label: "Referrer", value: referrerValue },
      { label: "Session Time Total", value: sessionTimeTotal },
    ],
    iconUrl,
    !!bubbleData.target.__data__.goalReached
  );

  d3.select("#tooltip").interrupt();

  // Was offset by exactly the bubble's own radius - fine for a large
  // historical dock bubble, but a small real-time bubble's radius (often
  // just 4-8px) put the tooltip right up against the cursor, making it hard
  // to actually click the bubble underneath without the tooltip itself
  // getting in the way. REALTIME_TOOLTIP_CURSOR_GAP adds real breathing
  // room on top of the radius, specific to this (real-time) tooltip only -
  // the historical dock tooltip below keeps its original radius-only offset
  // since its bubbles are large enough that this was never reported there.
  const REALTIME_TOOLTIP_CURSOR_GAP = 14;
  d3.select("#tooltip")
    .attr("class", "historical-cluster-tooltip tooltip-v2")
    .html(html)
    .style("opacity", 1)
    .style("background", rgbToHex(setColor(bubbleData.target.__data__.color)))
    .style("border", "none")
    .style(
      "left",
      bubbleData.layerX + bubbleData.target.__data__.radius + REALTIME_TOOLTIP_CURSOR_GAP + "px"
    )
    .style(
      "top",
      bubbleData.layerY - bubbleData.target.__data__.radius - REALTIME_TOOLTIP_CURSOR_GAP + "px"
    );

  const liveCard = document.getElementById("tooltip");
  if (liveCard) {
    // where the pointer is, on screen: a real event has it; the density-cloud's hover (and a long press) hands over only the position inside the stage, which is measured from the stage's own corner
    const host = document.getElementById("main_svg")?.getBoundingClientRect();
    const pointerX = typeof bubbleData.clientX === "number" ? bubbleData.clientX : (host?.left ?? 0) + bubbleData.layerX;
    const pointerY = typeof bubbleData.clientY === "number" ? bubbleData.clientY : (host?.top ?? 0) + bubbleData.layerY;
    if (Number.isFinite(pointerX) && Number.isFinite(pointerY)) keepTooltipOffVisitors(liveCard, pointerX, pointerY, bubbleData.target.__data__?.visitor_id);
  }
  clampTooltipToViewport();

  d3.select("#tooltip").transition().duration(200);
};

export const showSelectedUrlTooltip = (itemThis) => {
  if (suppressForTouch()) {
    return;
  }
  if (itemThis && itemThis.target.getAttribute("is-wrap")) {
    d3.select("#tooltip").interrupt();
    // The page's own path, as TEXT (it comes from a visitor's address bar, so it is never parsed
    // as HTML), then what a click on the label does.
    const tip = d3.select("#tooltip").attr("class", "selected-url-tooltip").text(itemThis.target?.getAttribute("real-text") ?? "");
    tip.append("div").style("margin-top", "4px").style("color", "#607d8b").text("Click for page metrics");
    tip
      .style("opacity", 1)
      .style("left", itemThis.layerX + "px")
      .style("top", itemThis.layerY + "px")
      .style("background", "#fff");

    clampTooltipToViewport();

    d3.select("#tooltip").transition().duration(200);
  }
};

export const hideRealtimeTooltip = () => {
  d3.select("#tooltip").transition().duration(500).style("opacity", 0);
};

export const hideHistoricalTooltip = () => {
  d3.select("#tooltip").transition().duration(500).style("opacity", 0);
};

/*
 * A hint on a page slot box ("Click to zoom in"). Its own element (not #tooltip, whose styling the other tooltips set and
 * reset), in the app's tooltip style (black, white text), and DELAYED: it appears only when the pointer has rested on the
 * box for SLOT_HINT_DELAY_MS, never on a pass over it.
 */
const SLOT_HINT_DELAY_MS = 700;
let slotHintTimer: ReturnType<typeof setTimeout> | null = null;
let slotHintEl: HTMLDivElement | null = null;
let slotHintAt: [number, number] = [0, 0];
const placeSlotHint = () => {
  if (!slotHintEl) return;
  slotHintEl.style.left = `${Math.min(slotHintAt[0] + 14, window.innerWidth - slotHintEl.offsetWidth - 8)}px`;
  slotHintEl.style.top = `${slotHintAt[1] + 18}px`;
};
export const scheduleSlotHint = (text: string, x: number, y: number, multiline = false) => {
  if (suppressForTouch()) return;
  hideSlotHint();
  slotHintAt = [x, y];
  slotHintTimer = setTimeout(() => {
    slotHintTimer = null;
    const el = document.createElement("div");
    el.setAttribute("role", "tooltip");
    el.textContent = text;
    el.style.cssText = `position:fixed;z-index:1003;pointer-events:none;background:#000;color:#fff;padding:6px 10px;border-radius:4px;font-size:12px;line-height:1.3;white-space:${multiline ? "pre" : "nowrap"};`;
    document.body.appendChild(el);
    slotHintEl = el;
    placeSlotHint();
  }, SLOT_HINT_DELAY_MS);
};
export const moveSlotHint = (x: number, y: number) => { slotHintAt = [x, y]; placeSlotHint(); };
export const hideSlotHint = () => {
  if (slotHintTimer) { clearTimeout(slotHintTimer); slotHintTimer = null; }
  if (slotHintEl) { slotHintEl.remove(); slotHintEl = null; }
};

export const hideSelectedUrlTooltip = () => {
  d3.select("#tooltip").transition().duration(500).style("opacity", 0);
};

// Rows are pre-built by the caller (clusterService knows whether this is a
// one-way "arrival" line or a two-way page-to-page line, and labels each
// row accordingly) rather than this function assuming a fixed From/To
// shape - a page-to-page line needs both directions' counts shown
// separately since recordPathTraversal merges A->B and B->A into one
// visual line/weight.
/**
 * The flow lines' tooltip (and the most common path's), in the app's own tooltip style: the `tooltip-v2` card with the `flow-tooltip` modifier (assets/theme/Styles.scss), a coloured
 * background and white text. The html comes from util/flowTooltips.ts, which escapes every page name. Opens FLOW_TOOLTIP_OFFSET px below and right of the pointer so it never covers it.
 */
/** what the flow card needs to keep off what matters: where the pointer is in the window, and the hovered ribbon as small rectangles (window coordinates) */
export interface FlowCardContext { pointer: { x: number; y: number }; ribbon: Rect[]; /** the slots of the boxes the ribbon joins */ linkedSlots: number[] }

let flowCardBoxes: Array<{ slot: number; rect: Rect }> = [];

/** the card takes the place around the pointer that hides the least (util/flowCardSpot.ts); runs after the usual place is set and before the window clamp */
let flowCardEvaluatedAt: { x: number; y: number } | null = null;

const placeFlowCard = (tooltip: HTMLElement, context: FlowCardContext | undefined, sticky = false) => {
  if (!context) return;
  const box = tooltip.getBoundingClientRect();
  if (!(box.width > 0)) return;
  flowCardEvaluatedAt = { x: context.pointer.x, y: context.pointer.y };
  const header = document.querySelector(".header");
  const stage = document.querySelector("[data-share-main]");
  const stageRect = stage ? stage.getBoundingClientRect() : null;
  const spot = chooseFlowCardSpot({
    size: { width: box.width, height: box.height },
    pointer: context.pointer,
    linked: flowCardBoxes.filter((b) => context.linkedSlots.includes(b.slot)).map((b) => b.rect),
    boxes: flowCardBoxes.filter((b) => !context.linkedSlots.includes(b.slot)).map((b) => b.rect),
    ribbon: context.ribbon,
    current: sticky ? { left: box.left, top: box.top } : null,
    view: { width: window.innerWidth, height: stageRect ? stageRect.bottom : window.innerHeight, top: stageRect ? stageRect.top + 8 : header ? header.getBoundingClientRect().bottom + 8 : 8 },
  });
  tooltip.style.left = (parseFloat(tooltip.style.left) || 0) + (spot.left - box.left) + "px";
  tooltip.style.top = (parseFloat(tooltip.style.top) || 0) + (spot.top - box.top) + "px";
};

/**
 * The pointer slides along the same ribbon: the card is weighed again (once the pointer has moved 20 px) and moves only if its place has become poor or a clearly better one exists, so a long ribbon never
 * leaves the card far behind or over the boxes, and a pointer held still never moves it (Chris, 2026-10-25).
 */
export const followFlowLineTooltip = (context: FlowCardContext) => {
  const tooltip = document.getElementById("tooltip");
  if (!tooltip || !flowCardEvaluatedAt) return;
  if (Math.hypot(context.pointer.x - flowCardEvaluatedAt.x, context.pointer.y - flowCardEvaluatedAt.y) < 20) return;
  placeFlowCard(tooltip, context, true);
  clampTooltipToViewport();
};

export const showFlowLineTooltip = (
  html: string,
  background: string,
  layerX: number,
  layerY: number,
  context?: FlowCardContext,
  keepPlace = false
) => {
  if (suppressForTouch()) {
    return;
  }
  d3.select("#tooltip").interrupt();
  d3.select("#tooltip")
    .attr("class", "historical-cluster-tooltip tooltip-v2 flow-tooltip")
    .html(html)
    .style("opacity", 1)
    .style("background", background)
    .style("border", "none");
  // a refresh of the numbers (the lines redrew under a still pointer) keeps the card where it is: it used to snap back to the usual place, and then jump again at the next move
  if (!keepPlace) {
    d3.select("#tooltip").style("left", layerX + FLOW_TOOLTIP_OFFSET + "px").style("top", layerY + FLOW_TOOLTIP_OFFSET + "px");
  }

  // the page boxes are read once per hover (they do not move while a ribbon is hovered); the card then takes the place that hides the least
  // (a refresh of the numbers under a still pointer is not a new hover: it must not forget the boxes or when the card was last weighed, or every redraw broke the placement that followed)
  if (!keepPlace) {
    flowCardEvaluatedAt = null;
    flowCardBoxes = context
      ? Array.from(document.querySelectorAll("svg.svg-main-window rect[rectIndex]")).map((el) => ({ slot: Number(el.getAttribute("rectIndex")), rect: el.getBoundingClientRect() })).filter((b) => b.rect.width > 0)
      : [];
    placeFlowCard(document.getElementById("tooltip") as HTMLElement, context);
  }
  clampTooltipToViewport();

  d3.select("#tooltip").transition().duration(200);
};

// mousemove fires on every pixel of cursor movement (dozens of times a
// second) - reusing showFlowLineTooltip there meant rebuilding the
// tooltip's innerHTML and spinning up a brand new D3 transition object on
// every single one of those events, which is what actually caused the
// hover slowdown, not the line-highlight logic. This just repositions the
// already-visible tooltip via a plain style write - no interrupt, no
// transition, no HTML rebuild.
export const repositionFlowLineTooltip = (layerX: number, layerY: number, context?: FlowCardContext) => {
  const tooltip = document.getElementById("tooltip");
  if (!tooltip) {
    return;
  }
  tooltip.style.left = layerX + FLOW_TOOLTIP_OFFSET + "px";
  tooltip.style.top = layerY + FLOW_TOOLTIP_OFFSET + "px";
  placeFlowCard(tooltip, context);
  clampTooltipToViewport();
};

export const hideFlowLineTooltip = () => {
  d3.select("#tooltip").transition().duration(500).style("opacity", 0);
};
